import React from "react";
import { useTranslation } from "react-i18next";
import { UserRelatedRecordsList, dsFieldCardStyle, dsLabelStyle } from "./user-related-records-list";

const RELATIONSHIP_TYPES = ["SPOUSE", "PARTNER", "PARENT", "CHILD", "SIBLING"];

const KIN_COLUMNS = [
  { id: "name", labelKey: "user_profile.next_of_kin.field.name" },
  {
    id: "relationship_type",
    labelKey: "user_profile.next_of_kin.field.relationship_type",
    renderValue: (row, t) =>
      row.relationship_type
        ? t(`user_profile.next_of_kin.relationship.${row.relationship_type}`, row.relationship_type)
        : t("user_profile.empty_value"),
  },
  { id: "phone_number", labelKey: "user_profile.next_of_kin.field.phone_number" },
  { id: "address", labelKey: "user_profile.next_of_kin.field.address" },
];

const INITIAL_FORM = {
  name: "",
  relationship_type: "",
  phone_number: "",
  address: "",
};

const validateKinForm = (form, t) => {
  const errors = {};
  if (!String(form.name || "").trim()) {
    errors.name = t("user_profile.related_records.validation.name_required");
  }
  if (!String(form.relationship_type || "").trim()) {
    errors.relationship_type = t("user_profile.next_of_kin.validation.relationship_required");
  }
  return errors;
};

const prepareKinRow = (data, mode, existing) => {
  if (mode === "form") {
    return {
      name: data.name || "",
      relationship_type: data.relationship_type || "",
      phone_number: data.phone_number || "",
      address: data.address || "",
    };
  }
  return {
    id: existing?.id ?? null,
    name: String(data.name || "").trim(),
    relationship_type: data.relationship_type || "",
    phone_number: data.phone_number || "",
    address: data.address || "",
    active: true,
  };
};

const KinFormFields = ({ formState, setFormState, formErrors, t }) => (
  <>
    <div>
      <label style={dsLabelStyle} htmlFor="kin-name">
        {t("user_profile.next_of_kin.field.name")}
      </label>
      <input
        id="kin-name"
        type="text"
        value={formState.name || ""}
        onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))}
        style={{
          ...dsFieldCardStyle,
          borderColor: formErrors.name ? "#DC2626" : "#E2E8F0",
        }}
        placeholder={t("user_profile.next_of_kin.field.name")}
      />
      {formErrors.name ? (
        <p style={{ margin: "6px 0 0", fontSize: 12, color: "#DC2626" }}>{formErrors.name}</p>
      ) : null}
    </div>

    <div>
      <label style={dsLabelStyle} htmlFor="kin-relationship">
        {t("user_profile.next_of_kin.field.relationship_type")}
      </label>
      <select
        id="kin-relationship"
        value={formState.relationship_type || ""}
        onChange={(e) => setFormState((prev) => ({ ...prev, relationship_type: e.target.value }))}
        style={{
          ...dsFieldCardStyle,
          borderColor: formErrors.relationship_type ? "#DC2626" : "#E2E8F0",
        }}
      >
        <option value="">{t("user_profile.next_of_kin.relationship.placeholder")}</option>
        {RELATIONSHIP_TYPES.map((type) => (
          <option key={type} value={type}>
            {t(`user_profile.next_of_kin.relationship.${type}`, type)}
          </option>
        ))}
      </select>
      {formErrors.relationship_type ? (
        <p style={{ margin: "6px 0 0", fontSize: 12, color: "#DC2626" }}>{formErrors.relationship_type}</p>
      ) : null}
    </div>

    <div>
      <label style={dsLabelStyle} htmlFor="kin-phone">
        {t("user_profile.next_of_kin.field.phone_number")}
      </label>
      <input
        id="kin-phone"
        type="tel"
        value={formState.phone_number || ""}
        onChange={(e) => setFormState((prev) => ({ ...prev, phone_number: e.target.value }))}
        style={dsFieldCardStyle}
        placeholder={t("user_profile.next_of_kin.field.phone_number")}
      />
    </div>

    <div>
      <label style={dsLabelStyle} htmlFor="kin-address">
        {t("user_profile.next_of_kin.field.address")}
      </label>
      <input
        id="kin-address"
        type="text"
        value={formState.address || ""}
        onChange={(e) => setFormState((prev) => ({ ...prev, address: e.target.value }))}
        style={dsFieldCardStyle}
        placeholder={t("user_profile.next_of_kin.field.address")}
      />
    </div>
  </>
);

export default function UserNextOfKinTable(props) {
  const { t } = useTranslation();
  const { setFieldValue, values, isLoading, disabled: disabledProp, syncKey, onPersistRows } = props;
  const name = props.name || "user_next_of_kins";

  return (
    <UserRelatedRecordsList
      name={name}
      syncKey={syncKey}
      setFieldValue={setFieldValue}
      values={values}
      isLoading={isLoading}
      disabled={disabledProp || values?.is_editable === false}
      columns={KIN_COLUMNS}
      emptyTitleKey="user_profile.next_of_kin.empty_title"
      emptyDescKey="user_profile.next_of_kin.empty_desc"
      initialFormState={INITIAL_FORM}
      renderFormFields={(ctx) => <KinFormFields {...ctx} t={t} />}
      validateForm={validateKinForm}
      prepareRow={prepareKinRow}
      onPersistRows={onPersistRows}
    />
  );
}

UserNextOfKinTable.defaultProps = {
  drag: false,
};
